import { useState, useRef, useEffect } from 'react'
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, Alert, Stack, Divider, LinearProgress, List, ListItem, ListItemText,
} from '@mui/material'
import { UploadFile, Analytics, CheckCircle } from '@mui/icons-material'
import { Modal, Button } from '@components/common'
import { toast } from 'react-toastify'
import api from '@services/api'

const POLL_INTERVAL = 1200

/**
 * Excel asset import. Analysis and seeding both run as background Celery tasks;
 * the dialog polls task status and shows a progress bar.
 *  1. Pick an .xlsx file and "Analyze" (dry run) -> preview what would change.
 *  2. Review, then "Import" to commit (reuses the same uploaded file).
 */
const AssetImportDialog = ({ open, onClose, onSuccess }) => {
  const [file, setFile] = useState(null)
  const [fileToken, setFileToken] = useState(null)
  const [phase, setPhase] = useState(null) // 'analyzing' | 'importing' | null
  const [progress, setProgress] = useState(null) // {processed, total}
  const [preview, setPreview] = useState(null)
  const fileInputRef = useRef(null)
  const pollRef = useRef(null)

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }

  useEffect(() => () => stopPolling(), [])

  const reset = () => {
    stopPolling()
    setFile(null)
    setFileToken(null)
    setPhase(null)
    setProgress(null)
    setPreview(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const busy = phase !== null

  const handleClose = () => {
    if (busy) return
    reset()
    onClose()
  }

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0]
    if (!selected) return
    if (!selected.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Please select an .xlsx workbook')
      return
    }
    setFile(selected)
    setFileToken(null) // new file -> force a fresh upload
    setPreview(null)
  }

  const pollStatus = (taskId, dryRun) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get('/assets/assets/import_status/', { params: { task_id: taskId } })
        const { state, progress: prog, result, error } = res.data
        if (state === 'PROGRESS' && prog) {
          setProgress({ processed: prog.processed, total: prog.total })
        } else if (state === 'SUCCESS') {
          stopPolling()
          setPhase(null)
          setProgress(null)
          if (dryRun) {
            setPreview(result)
          } else {
            const t = result?.totals || {}
            toast.success(`Imported ${t.created || 0} new and updated ${t.updated || 0} assets`)
            reset()
            onSuccess?.()
            onClose()
          }
        } else if (state === 'FAILURE') {
          stopPolling()
          setPhase(null)
          setProgress(null)
          toast.error(error || 'Import failed. Please try again.')
        }
        // PENDING / STARTED -> keep polling
      } catch {
        stopPolling()
        setPhase(null)
        setProgress(null)
        toast.error('Lost connection to the import task.')
      }
    }, POLL_INTERVAL)
  }

  const runImport = async (dryRun) => {
    if (!file && !fileToken) {
      toast.error('Select a workbook first')
      return
    }
    const data = new FormData()
    if (fileToken) {
      data.append('file_token', fileToken)
    } else {
      data.append('file', file)
    }
    data.append('dry_run', dryRun ? 'true' : 'false')

    setPhase(dryRun ? 'analyzing' : 'importing')
    setProgress(null)
    try {
      const res = await api.post('/assets/assets/import_excel/', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      if (res.data.file_token) setFileToken(res.data.file_token)
      pollStatus(res.data.task_id, dryRun)
    } catch (error) {
      setPhase(null)
      const msg = error.response?.data?.error || 'Could not start the import.'
      toast.error(msg)
    }
  }

  const totals = preview?.totals
  const fk = preview?.fk_created
  const pct = progress?.total ? Math.min(100, Math.round((progress.processed / progress.total) * 100)) : null
  const importErrors = preview?.errors || []
  const nothingToImport = totals ? (totals.created + totals.updated) === 0 : false

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Import Assets from Excel"
      maxWidth="md"
      disableBackdropClick={busy}
      actions={
        <>
          <Button variant="outlined" onClick={handleClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            startIcon={<Analytics />}
            onClick={() => runImport(true)}
            loading={phase === 'analyzing'}
            disabled={(!file && !fileToken) || phase === 'importing'}
          >
            {preview ? 'Re-analyze' : 'Analyze (Dry Run)'}
          </Button>
          <Button
            variant="contained"
            startIcon={<CheckCircle />}
            onClick={() => runImport(false)}
            loading={phase === 'importing'}
            disabled={!preview || phase === 'analyzing' || nothingToImport}
          >
            {preview ? `Import ${totals?.created || 0} New / Update ${totals?.updated || 0}` : 'Import'}
          </Button>
        </>
      }
    >
      <Stack spacing={2}>
        <Alert severity="info">
          Upload the inventory workbook (.xlsx). Analyze first to preview exactly what
          will be created, updated, and which new categories, manufacturers, facilities
          and locations will be added. Both steps run in the background. Re-importing the
          same workbook updates existing assets instead of duplicating them.
        </Alert>

        <Alert severity="info" icon={false}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Expected columns per site sheet
          </Typography>
          <Typography variant="body2" component="div">
            <strong>Required:</strong> SN, Description.{' '}
            A sheet missing either column is skipped (no rows imported).
          </Typography>
          <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
            <strong>Optional:</strong> Model, Manufacturer, Category, Serial No,
            Engraved Number/Tag #, Location, Room number, Status, Comments.
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            The header row is the one whose first cell is “SN”. Analyze validates these
            columns before anything is written.
          </Typography>
        </Alert>

        <Box>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            id="asset-import-file"
          />
          <label htmlFor="asset-import-file">
            <Button component="span" variant="outlined" startIcon={<UploadFile />} disabled={busy}>
              {file ? 'Change File' : 'Choose .xlsx File'}
            </Button>
          </label>
          {file && (
            <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
              Selected: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(0)} KB)
            </Typography>
          )}
        </Box>

        {busy && (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {phase === 'analyzing' ? 'Analyzing workbook…' : 'Importing assets…'}
              {progress?.total ? ` ${progress.processed} / ${progress.total} rows` : ''}
            </Typography>
            <LinearProgress variant={pct === null ? 'indeterminate' : 'determinate'} value={pct ?? 0} />
          </Box>
        )}

        {preview && !busy && (
          <>
            <Divider />
            {importErrors.length > 0 && (
              <Alert severity="error">
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Column problems found — fix the workbook and re-analyze
                </Typography>
                <List dense disablePadding>
                  {importErrors.map((msg, i) => (
                    <ListItem key={i} disableGutters sx={{ py: 0 }}>
                      <ListItemText primary={msg} />
                    </ListItem>
                  ))}
                </List>
                {nothingToImport && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    No rows can be imported until these are resolved.
                  </Typography>
                )}
              </Alert>
            )}

            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Preview {preview.dry_run ? '(nothing saved yet)' : ''}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip color="success" label={`${totals.created} to create`} />
                <Chip color="primary" label={`${totals.updated} to update`} />
                {totals.merged > 0 && (
                  <Chip color="info" label={`${totals.merged} identical rows folded into units`} />
                )}
                {totals.skipped > 0 && (
                  <Chip color="warning" label={`${totals.skipped} skipped (no description)`} />
                )}
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                New reference data to be seeded
              </Typography>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemText
                    primary={`Categories (${fk.categories.length})`}
                    secondary={fk.categories.join(', ') || 'None — all reused'}
                  />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText
                    primary={`Manufacturers (${fk.manufacturers.length})`}
                    secondary={fk.manufacturers.join(', ') || 'None — all reused'}
                  />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText
                    primary={`Facilities (${fk.facilities.length})`}
                    secondary={fk.facilities.join(', ') || 'None — all reused'}
                  />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText
                    primary={`Locations (${fk.locations.length})`}
                    secondary={
                      fk.locations.length
                        ? `${fk.locations.slice(0, 8).join(', ')}${fk.locations.length > 8 ? '…' : ''}`
                        : 'None — all reused'
                    }
                  />
                </ListItem>
              </List>
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Per-sheet breakdown
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Sheet</TableCell>
                    <TableCell align="right">Create</TableCell>
                    <TableCell align="right">Update</TableCell>
                    <TableCell align="right">Skipped</TableCell>
                    <TableCell align="right">Warnings</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.sheets.map((s) => (
                    <TableRow key={s.sheet}>
                      <TableCell>{s.sheet}</TableCell>
                      <TableCell align="right">{s.created}</TableCell>
                      <TableCell align="right">{s.updated}</TableCell>
                      <TableCell align="right">{s.skipped}</TableCell>
                      <TableCell align="right">{s.warnings.length}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {preview.sheets.some((s) => s.warnings.length > 0) && (
              <Alert severity="warning">
                {preview.sheets.reduce((n, s) => n + s.warnings.length, 0)} rows have data
                issues (e.g. missing/unknown category or status). They will still import
                using sensible defaults (Uncategorized / status A).
              </Alert>
            )}
          </>
        )}
      </Stack>
    </Modal>
  )
}

export default AssetImportDialog
